import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api/types";
import { InvoiceStatus } from "services/api/fleet/vehicles/invoices/types";
import { transformInvoiceStatus } from "services/api/fleet/vehicles/invoices/transformations";
import {
  InvoiceIncoming,
  InvoiceIncomingCostAllocation,
  InvoiceIncomingEditFormValues,
  InvoiceIncomingRejection,
  InvoiceIncomingRow,
  InvoiceIncomingDocumentQueue,
  InvoiceIncomingExtraction,
} from "./types";
import {
  transformInvoiceIncoming,
  transformInvoiceIncomingCostAllocation,
  transformInvoiceIncomingOnCreate,
  transformInvoiceIncomingOnUpdate,
  transformInvoiceIncomingRow,
  transformInvoiceIncomingDocumentQueue,
  transformInvoiceIncomingExtraction,
} from "./transformations";

/*---------------------------------------------------------------------------------------
   GET - INCOMING INVOICE STATUSES
-----------------------------------------------------------------------------------------*/
export const getInvoiceIncomingStatusesAPI = async (): Promise<InvoiceStatus[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data?.map(transformInvoiceStatus) ?? [];
};

/*---------------------------------------------------------------------------------------
   GET - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const getInvoiceIncomingAPI = async (
  id: string
): Promise<InvoiceIncoming> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformInvoiceIncoming(response.data);
};

/*---------------------------------------------------------------------------------------
   SEARCH - INCOMING INVOICES
-----------------------------------------------------------------------------------------*/
export const getInvoiceIncomingsAPI = async (
  pagination: Pagination,
  filters?: {
    search?: string | null;
    statusId?: string | null;
    incomingSourceId?: string | null;
    allocationStatus?: string | null;
    vehicleId?: string | null;
    invoiceDateFrom?: Date | null;
    invoiceDateTo?: Date | null;
    dueDateFrom?: Date | null;
    dueDateTo?: Date | null;
    totalMin?: number | null;
    totalMax?: number | null;
  },
): Promise<DataWithPagination<InvoiceIncomingRow>> => {

  let path = `/v1/fleet/vehicles/invoices/incoming?`;

  if (filters?.search) path += `search=${encodeURIComponent(filters.search)}&`;
  if (filters?.statusId) path += `statusId=${filters.statusId}&`;
  if (filters?.incomingSourceId) path += `incomingSourceId=${filters.incomingSourceId}&`;
  if (filters?.allocationStatus) path += `allocationStatus=${filters.allocationStatus}&`;
  if (filters?.vehicleId) path += `vehicleId=${filters.vehicleId}&`;
  if (filters?.invoiceDateFrom) path += `dateFrom=${filters.invoiceDateFrom.toISOString()}&`;
  if (filters?.invoiceDateTo) path += `dateTo=${filters.invoiceDateTo.toISOString()}&`;
  if (filters?.dueDateFrom) path += `dueDateFrom=${filters.dueDateFrom.toISOString()}&`;
  if (filters?.dueDateTo) path += `dueDateTo=${filters.dueDateTo.toISOString()}&`;
  if (filters?.totalMin) path += `totalMin=${filters.totalMin}&`;
  if (filters?.totalMax) path += `totalMax=${filters.totalMax}&`;

  return await paginatedApiCall(
    path,
    pagination,
    transformInvoiceIncomingRow
  );
};

/*---------------------------------------------------------------------------------------
   CREATE - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const createInvoiceIncomingAPI = async (
  formValues: InvoiceIncomingEditFormValues,
  t: (key: string) => string
): Promise<InvoiceIncoming> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming`,
      method: "POST",
      body: transformInvoiceIncomingOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.finance.incoming-invoice.create.message.success")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformInvoiceIncoming(response.data);
};

/*---------------------------------------------------------------------------------------
   UPDATE - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const updateInvoiceIncomingAPI = async (
  id: string,
  formValues: InvoiceIncomingEditFormValues,
  t: (key: string) => string
): Promise<InvoiceIncoming> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}`,
      method: "PUT",
      body: transformInvoiceIncomingOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.finance.incoming-invoice.edit.message.success")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformInvoiceIncoming(response.data);
};

/*---------------------------------------------------------------------------------------
   DELETE - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const deleteInvoiceIncomingAPI = async (
  id: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.finance.incoming-invoice.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   APPROVE - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const approveInvoiceIncomingAPI = async (
  id: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}/approve`,
      method: "POST",
    },
    {
      getMessage: {
        success: () => t("admin.finance.incoming-invoice.message.approve"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return !!response.data;
};

/*---------------------------------------------------------------------------------------
   REJECT - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
/**
 * Odbijanje e-računa se fiskalizira kod Porezne (čl. 52), pa je razlog obavezan za račune
 * zaprimljene kroz providera; za ručno unesene ga backend ne traži.
 */
export const rejectInvoiceIncomingAPI = async (
  id: string,
  rejection: InvoiceIncomingRejection | null,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}/reject`,
      method: "POST",
      body: rejection ? { reasonType: rejection.reasonType, reason: rejection.reason } : undefined,
    },
    {
      getMessage: {
        success: () => t("admin.finance.incoming-invoice.message.reject"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return !!response.data;
};

/*---------------------------------------------------------------------------------------
   GET - INCOMING INVOICE XML
-----------------------------------------------------------------------------------------*/
/** UBL zaprimljenog e-računa — postoji samo za račune zaprimljene kroz providera. */
export const getInvoiceIncomingXmlAPI = async (id: string): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}/xml`,
      method: "GET",
      responseType: "text",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
   MARK PAID - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const markPaidInvoiceIncomingAPI = async (
  id: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${id}/mark-paid`,
      method: "POST",
    },
    {
      getMessage: {
        success: () => t("admin.finance.incoming-invoice.message.markpaid"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return !!response.data;
};

/*---------------------------------------------------------------------------------------
   PAYMENTS - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const createInvoiceIncomingPaymentAPI = async (
  invoiceId: string,
  data: { amount: number; paidAt: Date; paymentMethodId?: string | null; paymentReference?: string | null; note?: string | null },
  t: (key: string) => string
): Promise<any> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${invoiceId}/payments`,
      method: "POST",
      body: data,
    },
    {
      getMessage: {
        success: () => "Uplata uspješno dodana",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

export const deleteInvoiceIncomingPaymentAPI = async (
  invoiceId: string,
  paymentId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${invoiceId}/payments/${paymentId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Uplata obrisana",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return !!response.data;
};

/*---------------------------------------------------------------------------------------
   GET COST ALLOCATIONS - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const getInvoiceIncomingAllocationsAPI = async (
  invoiceId: string
): Promise<InvoiceIncomingCostAllocation[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${invoiceId}/allocations`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return (response.data ?? []).map(transformInvoiceIncomingCostAllocation);
};

/*---------------------------------------------------------------------------------------
   ALLOCATE COSTS - INCOMING INVOICE
-----------------------------------------------------------------------------------------*/
export const allocateInvoiceIncomingCostsAPI = async (
  invoiceId: string,
  items: InvoiceIncomingCostAllocation[],
  t: (key: string) => string
): Promise<any> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/${invoiceId}/allocate-costs`,
      method: "POST",
      body: { items },
    },
    {
      getMessage: {
        success: () => "Troškovi uspješno raspodijeljeni",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
   UČITAVANJE RAČUNA IZ DATOTEKE
-----------------------------------------------------------------------------------------*/
/**
 * Od već učitanih datoteka slaže red za obradu. ZIP poslužitelj raspakira i u red ulaze datoteke
 * iz njega, pa jedan odabir može dati više računa.
 */
export const buildInvoiceIncomingDocumentQueueAPI = async (
  uploadIds: string[]
): Promise<InvoiceIncomingDocumentQueue> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/documents/queue`,
      method: "POST",
      body: { uploadIds },
    },
    {
      getMessage: {
        error: (error: any) => transformErrorResponse(error),
      },
    }
  )();

  return transformInvoiceIncomingDocumentQueue(response.data);
};

/** Čita podatke s učitane datoteke; ne sprema ništa dok korisnik ne potvrdi. */
export const extractInvoiceIncomingDocumentAPI = async (
  uploadId: string
): Promise<InvoiceIncomingExtraction> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/incoming/documents/${uploadId}/extract`,
      method: "POST",
    },
    {
      getMessage: {
        error: (error: any) => transformErrorResponse(error),
      },
    }
  )();

  return transformInvoiceIncomingExtraction(response.data);
};
